import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Calendar, User } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cf82e0fe-468c-4a43-97a9-405e8da70dca/artifacts/znleb4a2_260207%20Logo%20Galeries%20Yess5zw.png";

const CATEGORY_COLORS = {
    "Style & Closet": "text-pink-500",
    "Event Updates": "text-pink-500",
    "Community": "text-pink-500",
    "Charity": "text-pink-500"
};

const ArticlePage = () => {
    const { articleId } = useParams();
    const { api } = useAuth();
    const navigate = useNavigate();
    const [article, setArticle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchArticle();
    }, [articleId]);

    const fetchArticle = async () => {
        try {
            const response = await api().get(`/news/${articleId}`);
            setArticle(response.data);
        } catch (error) {
            console.error('Failed to fetch article:', error);
            setError('Article not found');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            month: 'long', 
            day: 'numeric',
            year: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-gray-200 border-t-gray-600 rounded-full animate-spin"></div>
            </div>
        );
    }

    if (error || !article) {
        return (
            <div className="min-h-screen bg-white flex flex-col items-center justify-center px-4">
                <p className="text-gray-500 mb-4">{error || 'Article not found'}</p>
                <button 
                    onClick={() => navigate('/news')}
                    className="text-sm text-gray-400 hover:text-gray-600"
                >
                    ← Back to News
                </button>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white pb-24 overflow-y-auto">
            {/* Fixed Header */}
            <div className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100 safe-area-top">
                <div className="flex items-center justify-between px-4 py-4">
                    <button
                        onClick={() => navigate('/menu?section=news')}
                        className="p-2 -ml-2 hover:bg-gray-100 rounded-full transition-colors"
                        data-testid="back-button"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <img 
                        src={LOGO_URL} 
                        alt="Galeries Yess" 
                        className="h-8 w-auto"
                    />
                    <div className="w-9" />
                </div>
            </div>

            {/* Header Image - starts below fixed header */}
            {article.header_image && (
                <div className="pt-16">
                    <div className="aspect-[16/10] overflow-hidden">
                        <img
                            src={article.header_image}
                            alt={article.title}
                            className="w-full h-full object-cover"
                        />
                    </div>
                </div>
            )}

            <div className={`px-5 ${article.header_image ? 'pt-6' : 'pt-24'}`}>
                {/* Category - completely hidden when there's a header image */}
                {!article.header_image && (
                    <span className="inline-block text-xs text-pink-500 uppercase tracking-wider mb-4">
                        {article.category}
                    </span>
                )}

                {/* Title */}
                <h1 className="text-2xl md:text-3xl font-heading font-medium text-gray-900 leading-tight mb-3">
                    {article.title}
                </h1>

                {/* Subtitle */}
                {article.subtitle && (
                    <p className="text-lg text-gray-500 italic mb-4">
                        {article.subtitle}
                    </p>
                )}

                {/* Meta */}
                <div className="flex items-center gap-4 text-sm text-gray-400 mb-8 pb-6 border-b border-gray-100">
                    {article.author_name && (
                        <span className="flex items-center gap-1">
                            <User className="w-4 h-4" />
                            {article.author_name}
                        </span>
                    )}
                    <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(article.published_at || article.created_at)}
                    </span>
                </div>

                {/* Article Content */}
                <div 
                    className="prose prose-sm max-w-none
                        prose-headings:font-heading prose-headings:font-medium
                        prose-p:text-gray-600 prose-p:leading-relaxed
                        prose-a:text-pink-600 prose-a:no-underline hover:prose-a:underline
                        prose-strong:text-gray-900
                        prose-img:rounded-lg prose-img:my-6
                        whitespace-pre-line text-gray-600 leading-relaxed"
                >
                    {article.content}
                </div>

                {/* Inline Images */}
                {article.inline_images && article.inline_images.length > 0 && (
                    <div className="mt-8 space-y-4">
                        {article.inline_images.map((img, index) => (
                            <div key={index} className="rounded-lg overflow-hidden">
                                <img
                                    src={img}
                                    alt={`${article.title} - Image ${index + 1}`}
                                    className="w-full h-auto"
                                />
                            </div>
                        ))}
                    </div>
                )}

                {/* Back Link */}
                <div className="mt-12 pt-6 border-t border-gray-100">
                    <button 
                        onClick={() => navigate('/menu?section=news')}
                        className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        ← Back to News
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ArticlePage;
